import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, InputAdornment, IconButton, CircularProgress, Link as MuiLink, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  LoginContainer,
  LoginCard,
  LogoContainer,
  Logo,
  Title,
  Subtitle,
  Form,
  ErrorMessage,
  SubmitButton,
  ForgotPassword,
} from './styles';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, error, loading } = useAuth();
  const { showSuccess } = useToast();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState('');

  
  useEffect(() => {
    // Verificar se o usuário acabou de completar o registro
    const registrationSuccess = localStorage.getItem('registration_success');
    if (registrationSuccess === 'true') {
      showSuccess('Cadastro completado com sucesso! Por favor, faça login com suas credenciais.');
      // Remover a flag do localStorage para não mostrar a mensagem novamente
      localStorage.removeItem('registration_success');
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação dos campos
    if (!username.trim()) {
      setValidationError('Usuário é obrigatório');
      return;
    }
    
    if (!password) {
      setValidationError('Senha é obrigatória');
      return;
    }
    
    setValidationError('');
    
    try {
      await login({ username, password });
      navigate('/hub');
    } catch (error) {
      // Erro já tratado pelo contexto de autenticação
      console.error('Falha ao efetuar login:', error);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <Logo src="/prefeitura_logo.png" alt="Prefeitura Logo" sx={{width: '15rem'}}/>
          <Title variant="h5">Bem-vindo</Title>
          <Subtitle variant="body2">Faça login para acessar o sistema</Subtitle>
        </LogoContainer>
        
        <Form onSubmit={handleLogin}>

          
          <TextField
            label="Usuário"
            variant="outlined"
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              input: { color: 'white' },
            }}
          />
          
          <TextField
            label="Senha"
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowPassword}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              input: { color: 'white' },
            }}
          />
          
          {(validationError || error) && (
            <ErrorMessage>{validationError || error}</ErrorMessage>
          )}
          
          <SubmitButton
            variant="contained"
            fullWidth
            type="submit"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
          </SubmitButton>
        </Form>
        <ForgotPassword variant="body2">
          <MuiLink
            component="button"
            variant="body2"
            onClick={() => navigate('/forgot-password')}
            sx={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Esqueceu sua senha?
          </MuiLink>
        </ForgotPassword>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;
